import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import HorizontalBars from "../../charts/HorizontalBars"
import { SERIES_COLORS } from "../../charts/palette"
import TrendLineChart from "../../charts/TrendLineChart"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import KpiBeside from "../../components/KpiBeside/KpiBeside"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { useCardData } from "../../hooks/useCardData"
import { getPercentChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount, formatDaysLong, formatDaysTick, formatDaysValue } from "../../utils/formats"

// The card's Tooltip terms, verbatim from the spec (§7 Time to Active).
const INFO = [
  {
    name: "Time to Active",
    formula:
      "Date of the first transition into Active in the log − date the record was created, in calendar days (UTC)"
  },
  { name: "Which records", formula: "Those that became Active for the first time in the period" },
  { name: "Result", formula: "Average, separately for CC and OI" }
]

// The Distribution bars in the spec's words (DAA-7-18).
const BUCKETS = [
  { id: "up_to_1", label: "≤ 1 day" },
  { id: "2_to_4", label: "2–4 days" },
  { id: "5_to_10", label: "5–10 days" },
  { id: "over_10", label: "> 10 days" }
]

const PROGRAMS = ["cc", "oi"]

// The design's trend: a 140px plot, plus the x-axis labels and the legend.
const CHART_HEIGHT = 220

// Time to Active (DAA-7-13 … DAA-7-18): how long a descriptor takes from creation to its first
// Active, per program — the averages with their weekly trend beside them, and the distribution of
// the records first activated in the period. Channels does not reach it (DAA-2-27). The answer holds
// only the programs the Program filter keeps.
const TimeToActiveCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("timeToActive")
  const kpis = data?.kpis
  const comparison = data?.comparison
  // The programs of the answer; both until the first one arrives.
  const programs = useMemo(() => (kpis ? Object.keys(kpis) : PROGRAMS), [kpis])

  // With one program selected the other program's KPI is hidden (DAA-7-16).
  const tiles = useMemo(
    () =>
      programs.map((program) => ({
        key: program,
        label: `${t("Average time to Active")} · ${program.toUpperCase()}`,
        value: kpis?.[program],
        decimals: 1,
        unit: t("days"),
        // A change badge only while comparing (DAA-2-22); a fall is good.
        badge: comparison ? (
          <ChangeBadge
            change={getPercentChange(kpis?.[program], comparison[program], GOOD_DIRECTION.DOWN)}
          />
        ) : null,
        secondary: comparison
          ? `vs ${formatDaysLong(comparison[program], t("days"))} ${t("prev. period")}`
          : null,
        note: program === "oi" ? t("handled offline through Verifi") : null
      })),
    [programs, kpis, comparison, t]
  )

  // No record activated in the period: the trend reads "No data available" (DAA-1-18). A week
  // without activations is 0, as every trend of the page (DAA-2-24).
  const series = useMemo(() => {
    if (!data?.records) return []

    return programs.map((program) => ({
      key: program,
      label: program.toUpperCase(),
      color: SERIES_COLORS[program],
      data: data.trend[program]
    }))
  }, [data, programs])

  const distributionRows = useMemo(() => {
    if (!data?.records) return []

    return BUCKETS.map((bucket) => ({
      key: bucket.id,
      label: t(bucket.label),
      value: data.distribution[bucket.id]
    }))
  }, [data, t])

  return (
    <MetricCard
      title={t("Time to Active")}
      info={INFO}
      notAffectedBy={t("Not affected by: Channels")}
      loading={loading}
    >
      <KpiBeside tiles={tiles} title={t("Trend")} tag={t("weekly average")}>
        <div style={{ height: CHART_HEIGHT }}>
          {data && (
            <TrendLineChart
              labels={data.labels}
              series={series}
              lastIntervalIncomplete={data.last_interval_incomplete}
              yFormat={formatDaysValue}
              yTickFormat={formatDaysTick}
              zoomY
              yMinStep={1}
              legend
              height={CHART_HEIGHT}
            />
          )}
        </div>
      </KpiBeside>
      <PanelRow>
        <Panel
          title={t("Distribution")}
          tag={
            data &&
            `${formatCount(data.records)} ${t("records activated for the first time in period")}`
          }
        >
          {data && <HorizontalBars rows={distributionRows} />}
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default TimeToActiveCard
